import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderStatus } from "../../../api/bseApi";

/**
 * Parse BSE Order Status response (pipe-separated, possibly multiple rows)
 * Single: OrderNo|SchemeName|TransactionType|Amount|Units|Status|ExecDate|Remarks
 * Multiple: rows separated by newline/cr
 */
function parseOrderStatusResponse(rawData) {
  if (!rawData || typeof rawData !== "string") return [];
  const rows = rawData.split(/\r?\n/).filter((r) => r && r.trim());
  const orders = [];
  for (const row of rows) {
    const parts = row.split("|").map((p) => (p || "").trim());
    const upper = row.toUpperCase();
    let status = "UNKNOWN";
    if (upper.includes("ALLOTTED") || upper.includes("EXECUTED")) status = "EXECUTED";
    else if (upper.includes("ACCEPTED") || upper.includes("PENDING")) status = "ACCEPTED";
    else if (upper.includes("REJECTED") || upper.includes("REJECT") || upper.includes("FAILED")) status = "REJECTED";
    else if (upper.includes("CANCELLED") || upper.includes("CANCEL")) status = "CANCELLED";

    orders.push({
      orderNo: parts[0] || "-",
      schemeName: parts[1] || "-",
      transactionType: parts[2] || "Purchase",
      amount: parts[3] || "",
      units: parts[4] || "",
      status,
      execDate: parts[6] || parts[5] || "",
      remarks: parts[7] || parts[6] || parts[5] || "",
    });
  }
  return orders;
}

/**
 * OrderStatusScreen - BSE StAR MF Order Status (MFAPI Flag 01)
 * Opens after Payment Status or from Dashboard / Order History
 * Receives: ClientCode (mandatory), OrderNo (optional)
 */
export default function OrderStatusScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(true);

  const clientCode = state?.clientCode || new URLSearchParams(location.search).get("clientCode");
  const orderNo = state?.orderNo || new URLSearchParams(location.search).get("orderNo");

  const fetchStatus = useCallback(async () => {
    if (!context?.clientCode) return null;
    try {
      const payload = { clientCode: context.clientCode };
      if (context.orderNo) payload.orderNo = context.orderNo;
      const res = await getOrderStatus(payload);
      if (res.data?.status === 1) {
        const parsed = parseOrderStatusResponse(res.data?.data);
        return parsed;
      }
      return null;
    } catch (err) {
      console.warn("Order status fetch failed:", err);
      return null;
    }
  }, [context?.clientCode, context?.orderNo]);

  useEffect(() => {
    if (clientCode) {
      setContext({ clientCode, orderNo: orderNo || null });
      setFetching(false);
      return;
    }
    if (userId) {
      const loadUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({ clientCode: String(ucc).trim(), orderNo: null });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      loadUser();
      return;
    }
    setError("ClientCode is required. Open from Payment Status or Order History.");
    setFetching(false);
  }, [clientCode, orderNo, userId, navigate]);

  useEffect(() => {
    if (!context?.clientCode) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const result = await fetchStatus();
      setLoading(false);
      if (result && Array.isArray(result)) {
        setOrders(result);
        if (result.length === 0) setError("No orders found.");
      } else {
        setError("Could not fetch order status.");
      }
    };

    load();
  }, [context?.clientCode, context?.orderNo, fetchStatus]);

  const handleRefresh = async () => {
    if (!context) return;
    setLoading(true);
    const result = await fetchStatus();
    setLoading(false);
    if (result && Array.isArray(result)) setOrders(result);
  };

  if (fetching || (!context && !error)) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3" />
              <h5>Client Code Required</h5>
              <p className="text-muted mb-3">{error}</p>
              <Link to="/user" className="btn btn-primary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderStatusBadge = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "EXECUTED" || s === "ALLOTTED")
      return <span className="badge bg-success fs-6"><i className="fas fa-check-circle me-1" /> {status}</span>;
    if (s === "ACCEPTED")
      return <span className="badge bg-info fs-6"><i className="fas fa-clock me-1" /> {status}</span>;
    if (s === "REJECTED" || s === "CANCELLED")
      return <span className="badge bg-danger fs-6"><i className="fas fa-times-circle me-1" /> {status}</span>;
    return <span className="badge bg-secondary fs-6">{status}</span>;
  };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Order Status</li>
              </ol>
            </nav>
            <h5>Order Status</h5>
            <p className="text-muted mb-0">
              Client: <strong>{context?.clientCode}</strong>
              {context?.orderNo && <> | Order: <strong>{context.orderNo}</strong></>}
            </p>
          </div>

          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "24px",
              border: "1px solid #e6eef0",
            }}
          >
            <nav className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="mb-0" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Order Status {context?.orderNo ? "(Single)" : "(All Orders)"}
              </h6>
              <button className="btn btn-outline-primary btn-sm" onClick={handleRefresh} disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-sync-alt me-1" />}
                Refresh
              </button>
            </nav>

            {loading && orders.length === 0 && (
              <div className="text-center py-5">
                <span className="spinner-border spinner-border-lg text-primary mb-3" role="status" />
                <p>Fetching order status...</p>
              </div>
            )}

            {error && orders.length === 0 && !loading && (
              <div className="alert alert-warning">
                <i className="fas fa-info-circle me-2" />
                {error}
                <div className="mt-3">
                  <button className="btn btn-outline-primary btn-sm me-2" onClick={handleRefresh}>Retry</button>
                  <Link to="/user" className="btn btn-outline-secondary btn-sm">Back to Users</Link>
                </div>
              </div>
            )}

            {orders.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Order No</th>
                      <th>Scheme</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Units</th>
                      <th>Status</th>
                      <th>Exec / NAV Date</th>
                      <th>Remarks</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order, i) => (
                      <tr key={order.orderNo + i}>
                        <td>{order.orderNo}</td>
                        <td>{order.schemeName}</td>
                        <td>{order.transactionType}</td>
                        <td>{order.amount ? `₹${Number(order.amount).toLocaleString()}` : "-"}</td>
                        <td>{order.units || "-"}</td>
                        <td>{renderStatusBadge(order.status)}</td>
                        <td>{order.execDate || "-"}</td>
                        <td className="small">{order.remarks || "-"}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {["REJECTED", "FAILED"].includes((order.status || "").toUpperCase()) && (
                              <Link
                                to="/user/order-rejection"
                                state={{ orderNo: order.orderNo, clientCode: context?.clientCode }}
                                className="btn btn-outline-danger btn-sm"
                              >
                                View Rejection
                              </Link>
                            )}
                            <Link
                              to="/user/child-order-status"
                              state={{ orderNo: order.orderNo, clientCode: context?.clientCode }}
                              className="btn btn-outline-primary btn-sm"
                            >
                              Child Orders
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {orders.length > 0 && (
              <div className="alert alert-light border mt-3">
                <h6 className="mb-2">Status Legend</h6>
                <p className="mb-0 small">
                  <strong>ACCEPTED:</strong> Order accepted, awaiting execution &nbsp;|&nbsp;
                  <strong>EXECUTED/ALLOTTED:</strong> Success – navigate to Portfolio / Holdings &nbsp;|&nbsp;
                  <strong>REJECTED/CANCELLED:</strong> See remarks for reason
                </p>
              </div>
            )}

            <div className="d-flex gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
              <Link to="/user/child-order-status" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Child Order Status</Link>
              <Link to="/user/allotment-statement" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Allotment Statement</Link>
              <Link to="/user/redemption-statement" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Redemption Statement</Link>
              <Link to="/payment/status" state={{ clientCode: context?.clientCode }} className="btn btn-outline-primary">Payment Status</Link>
              <Link to="/user" className="btn btn-secondary">Back to Users</Link>
              {orders.some((o) => ["EXECUTED", "ALLOTTED"].includes((o.status || "").toUpperCase())) && (
                <Link to="/user" className="btn btn-primary">Portfolio / Holdings</Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
